/** Normaliza readings de tensão: séries fase-neutro (F-N) e fase-fase (F-F) + fixed_values do schema. */
const VOLTAGE_PHASE_KEYS = ['voltage_a', 'voltage_b', 'voltage_c'] as const
const VOLTAGE_LINE_KEYS = ['voltage_ab', 'voltage_bc', 'voltage_ca'] as const
const VOLTAGE_KEYS = [...VOLTAGE_PHASE_KEYS, ...VOLTAGE_LINE_KEYS] as const

export interface NormalizedVoltageReadings {
  voltage_a: [number, number][]
  voltage_b: [number, number][]
  voltage_c: [number, number][]
  voltage_ab: [number, number][]
  voltage_bc: [number, number][]
  voltage_ca: [number, number][]
  fixed: Record<string, number>
}

function parseFixedValue(value: unknown): number | null {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isNaN(parsed) ? null : parsed
  }
  return null
}

export function normalizeVoltageReadingsForChart(
  readings: Record<string, Array<{ t?: string; v?: number }>> | null | undefined,
  fixedValues?: Record<string, unknown> | null,
): NormalizedVoltageReadings {
  const empty: NormalizedVoltageReadings = {
    voltage_a: [],
    voltage_b: [],
    voltage_c: [],
    voltage_ab: [],
    voltage_bc: [],
    voltage_ca: [],
    fixed: {},
  }
  if (!readings || typeof readings !== 'object') {
    return empty
  }

  const seriesKeys = VOLTAGE_KEYS.filter(
    (k) => Array.isArray(readings[k]) && (readings[k] as Array<{ t?: string; v?: number }>).length > 0,
  )

  const byTime: Record<string, Record<string, number>> = {}
  seriesKeys.forEach((k) => {
    ;(readings[k] as Array<{ t?: string; v?: number }>).forEach((point) => {
      const t = point.t
      if (!t) return
      const v = point.v
      if (typeof v !== 'number' || Number.isNaN(v)) return
      if (!byTime[t]) byTime[t] = {}
      byTime[t][k] = v
    })
  })

  const present = new Set<string>(seriesKeys)
  const times = Object.keys(byTime).sort()
  const result: NormalizedVoltageReadings = {
    voltage_a: [],
    voltage_b: [],
    voltage_c: [],
    voltage_ab: [],
    voltage_bc: [],
    voltage_ca: [],
    fixed: {},
  }

  times.forEach((t) => {
    const row = byTime[t]
    const ts = new Date(t).getTime()
    VOLTAGE_KEYS.forEach((k) => {
      if (present.has(k)) result[k].push([ts, row[k] ?? null])
    })
  })

  if (fixedValues && typeof fixedValues === 'object') {
    VOLTAGE_KEYS.forEach((k) => {
      const parsed = parseFixedValue(fixedValues[k])
      if (parsed !== null) result.fixed[k] = parsed
    })
  }

  return result
}
